import { requireService, wrap } from '@repo/di-ez';
import { CameraLight, ExpandLeftLight, FullArrowLight, ReduceLight } from '@repo/go-icons';
import { Button, Switch } from '@repo/go-ui';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Trajectory2dChartsLayout } from './trajectory-2d-charts-layout';
import { Trajectory3dChart } from './trajectory-3d-chart';
import styles from './trajectory-graph.module.scss';

export const TrajectoryGraph = wrap(function TrajectoryGraph() {
  const { t } = useTranslation();
  const uiStore = requireService('trajectoryUiStore');
  const graphStore = requireService('trajectoryGraphStore');

  const hasChartData =
    graphStore.chartDimension === '2d'
      ? graphStore.graphTrajectories.some((trajectory) => trajectory.entity.points.length > 0)
      : graphStore.mainTrajectory3dLines.length > 0;

  useEffect(() => {
    return () => {
      uiStore.flushCameraState();
    };
  }, [uiStore]);

  return (
    <div className={clsx(styles.graphRoot, styles[`graphRoot${uiStore.expandState}`])}>
      <header className={styles.graphHeader}>
        <Button
          icon={<ExpandLeftLight />}
          variant="flat"
          size="m"
          className={clsx(
            styles.baseBtns,
            styles.graphCollapseButton,
            styles[`graphCollapseButton${uiStore.expandState}`],
          )}
          onClick={() => {
            const newState = (() => {
              if (uiStore.expandState === 'tableExpanded') {
                return 'default';
              }

              return 'tableExpanded';
            })();

            uiStore.setExpandState(newState);
          }}
        />
        <Switch
          checked={graphStore.chartDimension === '3d'}
          size="m"
          className={styles.chartDimensionSwitch}
          leftNode={<span className="caption">2D</span>}
          rightNode={<span className="caption">3D</span>}
          onChange={(checked) => {
            graphStore.setChartDimension(checked ? '3d' : '2d');
            uiStore.emitStateChange();
          }}
        />
        <div className={styles.headerActions}>
          <Button
            size="m"
            className={styles.baseBtns}
            icon={<CameraLight />}
            variant="flat"
            disabled={!graphStore.canDownloadScreenshot}
            title={t('Graph.downloadScreenshot')}
            onClick={() => graphStore.downloadScreenshot()}
          />
          {uiStore.expandState === 'default' && (
            <Button
              size="m"
              className={styles.baseBtns}
              icon={<FullArrowLight />}
              variant="flat"
              onClick={() => uiStore.setExpandState('graphExpanded')}
            />
          )}

          {uiStore.expandState === 'graphExpanded' && (
            <Button
              size="m"
              className={styles.baseBtns}
              icon={<ReduceLight />}
              variant="flat"
              onClick={() => uiStore.setExpandState('default')}
            />
          )}
        </div>
      </header>
      <div className={styles.graphContent}>
        {hasChartData ? (
          graphStore.chartDimension === '2d' ? (
            <Trajectory2dChartsLayout />
          ) : (
            <Trajectory3dChart />
          )
        ) : (
          <div className={clsx('dash-body-regular', styles.emptyState)}>{t('Graph.noData')}</div>
        )}
      </div>
    </div>
  );
});
